import React, { useRef, useState } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Camera, Image as ImageIcon, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";

export default function PhotoUpload({ returnId, photos = [], onChange, disabled = false }) {
  const { t } = useLang();
  const cameraRef = useRef();
  const galleryRef = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/returns/${returnId}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange && onChange([...(photos || []), data]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const remove = async (photoId) => {
    try {
      await api.delete(`/returns/${returnId}/photos/${photoId}`);
      onChange && onChange((photos || []).filter((p) => p.id !== photoId));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="photo-upload">
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="photo-camera-input"
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="photo-gallery-input"
        />
        <Button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={disabled || uploading}
          variant="outline"
          className="flex-1 sm:flex-none"
          style={{ borderRadius: 4 }}
          data-testid="photo-camera-button"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
          {t("form.photoUpload")}
        </Button>
        <Button
          type="button"
          onClick={() => galleryRef.current?.click()}
          disabled={disabled || uploading}
          variant="outline"
          className="flex-1 sm:flex-none"
          style={{ borderRadius: 4 }}
          data-testid="photo-gallery-button"
        >
          <ImageIcon className="w-4 h-4 mr-2" />
          Galerie
        </Button>
      </div>

      {photos && photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid">
          {photos.map((p) => (
            <div key={p.id} className="relative group swiss-card overflow-hidden" data-testid={`photo-item-${p.id}`}>
              <img src={fileUrl(p.url)} alt="Retoure" className="w-full h-32 object-cover" />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="absolute top-1 right-1 bg-white/95 p-1.5 border border-border opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ borderRadius: 4 }}
                  aria-label={t("form.deletePhoto")}
                  data-testid={`photo-delete-${p.id}`}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
